import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Lock, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cleanupAuthState, signOutGlobally, redirectToCorrectDashboard } from "@/lib/auth-utils";
import { BackToHome } from "@/components/BackToHome";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";


const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const { toast } = useToast();

  const SECURITY_QUESTIONS = [
    "What was the name of your first pet?",
    "What city were you born in?",
    "What was your mother's maiden name?",
    "What was the name of your first school?",
    "What was your childhood nickname?",
    "What street did you live on as a child?",
    "What was the make of your first car?",
    "What is your favorite movie?",
    "What was the name of your best friend in high school?",
    "What is your favorite food?"
  ];

  useEffect(() => {
    document.title = "Sign In - Ascendry";
  }, []);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      // Clean up any existing auth state
      cleanupAuthState();
      
      // Attempt global sign out first
      try {
        await signOutGlobally();
      } catch (err) {
        // Continue even if this fails
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      if (data.user) {

        toast({
          title: "Welcome back!",
          description: "You've been signed in successfully.",
        });

        // Redirect to correct dashboard based on user role
        await redirectToCorrectDashboard(data.user.id);
      }
    } catch (error: any) {
      toast({
        title: "Sign in failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const displayName = formData.get("displayName") as string;

    console.log('🔵 Starting signup process for:', email);

    // Validate security question
    if (!securityQuestion || !securityAnswer.trim()) {
      toast({
        title: "Security question required",
        description: "Please select a security question and provide an answer.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard/student`,
          data: {
            display_name: displayName,
            first_name: displayName,
            role: 'student',
          },
        },
      });

      console.log('🔵 Signup response:', { data, error });

      if (error) throw error;

      if (data.user) {
        console.log('🔵 Saving security question for user:', data.user.id);
        
        // If Supabase returned a session (no email verification), go straight to dashboard
        if (data.session?.user) {
          toast({ title: "Welcome!", description: "Your account is ready." });
          await redirectToCorrectDashboard(data.session.user.id);
          return;
        }
        
        // Save security question
        const { error: securityError } = await supabase.functions.invoke('save-security-question', {
          body: {
            question: securityQuestion,
            answer: securityAnswer,
            user_id: data.user.id
          }
        });

        if (securityError) {
          console.error('🔴 Security question save error:', securityError);
          toast({
            title: "Warning",
            description: "Account created but security question failed to save. You can set it up later.",
            variant: "destructive",
          });
        } else {
          console.log('🔵 Security question saved successfully');
        }
      }

      console.log('🔵 Signup successful, attempting auto sign-in');
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) {
        toast({ title: "Account created", description: "You can now sign in.", });
      } else {
        toast({ title: "Welcome!", description: "Your account is ready.", });
        await redirectToCorrectDashboard(signInData.user!.id);
      }

    } catch (error: any) {
      console.error('🔴 Signup error:', error);
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <BackToHome variant="link" />

        <Card className="obsidian-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-energy">Welcome to Ascendry</CardTitle>
            <CardDescription>
              Connect with expert mentors and accelerate your growth
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="space-y-4">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signin-email"
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signin-password"
                        name="password"
                        type="password"
                        placeholder="Enter your password"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup" className="space-y-4">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Display Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-name"
                        name="displayName"
                        type="text"
                        placeholder="Enter your name"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-email"
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-password"
                        name="password"
                        type="password"
                        placeholder="Create a password"
                        className="pl-10"
                        required
                        minLength={6}
                      />
                    </div>
                   </div>
                   
                   {/* DEBUG: Security Questions Section */}
                   <div className="space-y-4 border-t pt-4 bg-yellow-100 p-2">
                     <div className="text-center">
                       <h3 className="font-medium text-sm text-foreground">Security Question</h3>
                       <p className="text-xs text-muted-foreground">For account recovery</p>
                     </div>
                     
                     <div className="space-y-2">
                       <Label htmlFor="security-question">Choose a security question</Label>
                       <Select value={securityQuestion} onValueChange={setSecurityQuestion} required>
                         <SelectTrigger>
                           <SelectValue placeholder="Select a security question" />
                         </SelectTrigger>
                         <SelectContent>
                           {SECURITY_QUESTIONS.map((question, index) => (
                             <SelectItem key={index} value={question}>
                               {question}
                             </SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                     </div>
                     
                     <div className="space-y-2">
                       <Label htmlFor="security-answer">Your answer</Label>
                       <Input
                         id="security-answer"
                         type="text"
                         placeholder="Enter your answer"
                         value={securityAnswer}
                         onChange={(e) => setSecurityAnswer(e.target.value)}
                         required
                       />
                     </div>
                   </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;